import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import { useDispatch, useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import useSessionStorageState from '../../../hooks/useSessionStorageState';
import BuzzInput from '../../../components/BuzzInput/BuzzInput';
import BuzzTabs from '../../../components/BuzzTab/BuzzTabs';
import BuzzTab from '../../../components/BuzzTab/BuzzTab';
import BuzzAutoCompleteSingleSelectInput from '../../../components/BuzzAutoCompleteInput/BuzzAutoCompleteSingleSelectInput';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import { sanitizePayload } from '../../../components/FormRenderer/utils';
import CurriculumFrameworkPicker, {
  CURRICULUM_FRAMEWORK_STORAGE_KEY,
  FrameworkRequiredNotice,
} from './CurriculumFrameworkPicker';
import CurriculumBulkImport from './CurriculumBulkImport';
import {
  createCurriculumNode,
  getCurriculumAgeGroups,
  getCurriculumNodes,
  getCurriculumNodesImportTemplate,
  importCurriculumNodes,
  updateCurriculumNode,
} from '../AkademySlice';

/**
 * Declared at module scope rather than inside the screen: a component redeclared per
 * render is a new type each time, so React would unmount and rebuild every row on every
 * keystroke in the search box.
 */
function NodeListItem({ node, ageGroupLabels, canAddChild, onAddChild, onEdit }) {
  const ageGroups = ageGroupLabels(node.ageGroupIds);
  const breadcrumb = (node.ancestorPath || []).map(({ value }) => value);

  return (
    <Box
      display="flex"
      alignItems="center"
      py={1}
      px={2}
      style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
      <Box flexGrow={1}>
        {breadcrumb.length > 0 && (
          <Typography variant="caption" color="textSecondary">
            {breadcrumb.join(' → ')} →
          </Typography>
        )}
        <Typography component="div">
          <Box fontWeight="fontWeightMedium">{node.value}</Box>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {node._id}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        justifyContent="flex-end"
        style={{ gap: '0.5rem' }}>
        <Chip size="small" label={`L${node.level} · ${node.levelName}`} />
        {ageGroups.map(({ _id, label }) => (
          <Chip key={_id} size="small" variant="outlined" label={label} />
        ))}
        {canAddChild && (
          <Tooltip title="Add a child node">
            <IconButton size="small" onClick={() => onAddChild(node)}>
              <AddIcon color="secondary" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={() => onEdit(node)}>
          <EditIcon color="secondary" />
        </IconButton>
      </Box>
    </Box>
  );
}

/**
 * Curriculum v2's node tree, which replaces v1's separate "Development Areas" and
 * "Indicators" screens — the area/indicator split collapsed into one variable-depth tree
 * whose depth is declared by the framework's own `levels`.
 *
 * A framework can hold thousands of nodes, and `GET nodes` has no pagination — fetching
 * the whole tree in one call is what caused the lag this screen used to have. So instead
 * of one big fetch, this screen is **one level at a time**: a level tab picks what to
 * display, and every level shallower than it gets its own on-demand fetch to populate a
 * cascading ancestor filter (level 1's items filter level 2, levels 1–2 filter level 3,
 * and so on).
 *
 * The narrowing itself is server-side: `GET nodes` takes one `level<N>Item=<value>` query
 * param per ancestor level (e.g. `level1Item=Physical%20Development`), matched against the
 * node's own level/value or its cached `ancestorPath` — so a level ≥ 2 fetch only ever
 * asks for the branch currently selected in the filters above it, instead of pulling every
 * node at that level and filtering them out again in the browser.
 */
function AkademyCurriculumNodes() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [frameworkId, setFrameworkId] = useSessionStorageState(
    CURRICULUM_FRAMEWORK_STORAGE_KEY,
    null
  );
  const [selectedLevel, setSelectedLevel] = useSessionStorageState(
    'curriculum.nodes.selectedLevel',
    null
  );
  const [ancestorFilters, setAncestorFilters] = useSessionStorageState(
    'curriculum.nodes.ancestorFilters',
    {}
  );
  const [resolvingDefaults, setResolvingDefaults] = useState(false);
  // Guards the framework-change effect below: on the very first run, if a level was
  // already restored from a previous session, keep it (and its filters) instead of
  // resetting to the framework's first level. Every subsequent framework change — an
  // actual pick, not a resumed session — still resets normally.
  const isInitialMount = useRef(true);
  const [nodesByLevel, setNodesByLevel] = useState({});
  const [refreshToken, setRefreshToken] = useState(0);
  const [search, setSearch] = useState('');
  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { curriculumAgeGroups, curriculumFrameworks } = useSelector(
    ({ akademyReducer }) => {
      return {
        curriculumAgeGroups: akademyReducer.curriculumAgeGroups,
        curriculumFrameworks: akademyReducer.curriculumFrameworks,
      };
    }
  );

  const framework = curriculumFrameworks.find(({ _id }) => _id === frameworkId);

  const levelOptions = [...((framework && framework.levels) || [])]
    .sort((a, b) => a.level - b.level)
    .map(({ level, name }) => {
      return { label: `L${level} — ${name}`, value: level };
    });

  // Switches to `newLevel` and, for level ≥ 2, defaults every ancestor filter to its first
  // value instead of leaving them on "All" — so clicking a deeper tab lands on a populated
  // table immediately rather than an unfiltered (and much larger) one. The defaults cascade
  // top-down: level 2's default depends on level 1's, level 3's on both, and so on, so they
  // resolve one level at a time rather than in parallel.
  //
  // `resolvingDefaults` holds the fetch effect below off entirely while this runs. Without
  // it, `selectedLevel` changing right away would fire that effect with the *old*
  // `ancestorFilters` (or, if cleared up front, none at all) — an unfiltered fetch of
  // `newLevel` itself, which is exactly the expensive one for a deep, high-cardinality
  // level. Only once `defaults` is fully resolved do `ancestorFilters` and the gate flip
  // together, so the effect fires exactly once, already filtered.
  const applyLevelSelection = async (newLevel) => {
    setSelectedLevel(newLevel);
    setSearch('');

    if (!newLevel || newLevel < 2) {
      setAncestorFilters({});
      return;
    }

    setResolvingDefaults(true);
    handleLoader(true);
    try {
      const defaults = {};
      for (const { value: level } of levelOptions.filter(({ value }) => value < newLevel)) {
        const params = { frameworkId, level };
        Object.entries(defaults).forEach(([filterLevel, filterValue]) => {
          params[`level${filterLevel}Item`] = filterValue;
        });

        const nodes = await dispatch(getCurriculumNodes(params)).unwrap();
        if (!nodes.length) break;
        defaults[level] = nodes[0].value;
      }
      setAncestorFilters(defaults);
    } catch (error) {
      console.error('rejected', error);
      ErrorToast(error.message);
    } finally {
      setResolvingDefaults(false);
      handleLoader(false);
    }
  };

  useEffect(() => {
    if (!frameworkId) return;

    dispatch(getCurriculumAgeGroups({ frameworkId }));
    setNodesByLevel({});

    const resumingPersistedSession = isInitialMount.current && selectedLevel !== null;
    isInitialMount.current = false;
    if (resumingPersistedSession) return;

    // Belt-and-braces: clears out any filter left over from the previous framework before
    // `applyLevelSelection` below has a chance to resolve fresh ones, so a stale
    // `level<N>Item` value can never leak into a fetch for the new framework.
    setAncestorFilters({});

    const firstLevel =
      framework && framework.levels && framework.levels.length
        ? [...framework.levels].sort((a, b) => a.level - b.level)[0].level
        : null;
    applyLevelSelection(firstLevel);
  }, [frameworkId]);

  // Fetches only the levels a level tab actually needs: the selected level itself (what's
  // displayed) plus every shallower level (the ancestor filter options). A whole framework
  // never gets fetched at once, and from level 2 down each fetch also carries a
  // `level<N>Item` param per ancestor filter set *above* it — so picking a Level 1 filter
  // re-fetches Level 2 (and any deeper level) already narrowed to that branch, rather than
  // fetching everything and filtering it out client-side.
  //
  // `framework` is in the dependency list (not just `frameworkId`) because a resumed
  // session can hydrate `frameworkId`/`selectedLevel` from sessionStorage synchronously,
  // ahead of `curriculumFrameworks` finishing its own async fetch — without it, this would
  // compute an empty `levelOptions` on that first pass and never re-run once the
  // framework's `levels` actually become available.
  useEffect(() => {
    if (!frameworkId || !selectedLevel || resolvingDefaults) return;

    const levelsNeeded = levelOptions
      .map(({ value }) => value)
      .filter((level) => level <= selectedLevel);

    handleLoader(true);
    Promise.all(
      levelsNeeded.map((level) => {
        const params = { frameworkId, level };
        Object.entries(ancestorFilters).forEach(([filterLevel, filterValue]) => {
          if (filterValue && Number(filterLevel) < level) {
            params[`level${filterLevel}Item`] = filterValue;
          }
        });
        return dispatch(getCurriculumNodes(params))
          .unwrap()
          .then((nodes) => [level, nodes]);
      })
    )
      .then((pairs) => {
        setNodesByLevel(Object.fromEntries(pairs));
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      })
      .finally(() => handleLoader(false));
  }, [
    frameworkId,
    selectedLevel,
    ancestorFilters,
    resolvingDefaults,
    refreshToken,
    framework,
  ]);

  const refresh = () => setRefreshToken((token) => token + 1);

  // A node carries a *set* of age groups, and the stored order is not meaningful — the
  // backend collapses duplicates and does not preserve what was sent. So both the row
  // chips and the picker options are sorted by the band itself, giving a node's bands a
  // stable reading order regardless of the order they were tagged in.
  const sortedAgeGroups = useMemo(
    () =>
      [...curriculumAgeGroups].sort(
        (a, b) => a.minMonth - b.minMonth || a.maxMonth - b.maxMonth
      ),
    [curriculumAgeGroups]
  );

  // Ids that no longer resolve to an age group are dropped rather than rendered as a
  // blank chip. Bands are keyed by id, not by label — nothing stops two age groups sharing
  // the same min/max, so the label is not a unique key.
  const ageGroupLabels = (ageGroupIds) =>
    sortedAgeGroups
      .filter(({ _id }) => (ageGroupIds || []).includes(_id))
      .map(({ _id, minMonth, maxMonth }) => {
        return { _id, label: `${minMonth}–${maxMonth} mo` };
      });

  const ageGroupOptions = sortedAgeGroups.map(({ _id, minMonth, maxMonth }) => {
    return { label: `${minMonth}–${maxMonth} months`, value: _id };
  });

  // Every node fetched so far across levels 1..selectedLevel — the only pool a "Parent"
  // picker can validly draw from, since a parent must be shallower than its child.
  const cachedNodesFlat = useMemo(
    () => Object.values(nodesByLevel).flat(),
    [nodesByLevel]
  );

  /**
   * A node cannot become its own ancestor, so the parent picker excludes the node itself
   * and its whole subtree. `ancestors` is the id array kept on every node precisely for
   * this kind of subtree question — no walking required.
   */
  const parentOptions = (nodeIdBeingEdited) => [
    { label: 'None — root node', value: null },
    ...cachedNodesFlat
      .filter(({ _id, ancestors }) => {
        if (!nodeIdBeingEdited) return true;
        return (
          _id !== nodeIdBeingEdited &&
          !(ancestors || []).includes(nodeIdBeingEdited)
        );
      })
      .map((node) => {
        const path = [...(node.ancestorPath || []).map(({ value }) => value), node.value];
        return { label: `${path.join(' → ')}  [L${node.level}]`, value: node._id };
      }),
  ];

  const buildFormTemplate = (nodeIdBeingEdited) => [
    {
      id: 'form.value',
      title: 'Value',
      slug: 'value',
      type: 'String',
      required: true,
    },
    {
      id: 'form.level',
      title: 'Level (must be deeper than the parent, shallower than any child)',
      slug: 'level',
      type: 'Select',
      options: levelOptions,
      required: true,
    },
    {
      id: 'form.parent',
      title: 'Parent',
      slug: 'parentId',
      type: 'Select',
      options: parentOptions(nodeIdBeingEdited),
    },
    // A node may be tagged with several age bands (Development Matters-style bands overlap
    // rather than partition, so "22–36 months" and "30–50 months" can both apply to the
    // same indicator). `ageGroupIds` is sent as the *whole* set on every save — the backend
    // replaces, it does not merge — so whatever is in this picker when Save is pressed is
    // exactly what the node ends up with, and clearing it drops every band.
    {
      id: 'form.ageGroups',
      title: 'Age Groups',
      slug: 'ageGroupIds',
      type: 'AutoCompleteMultiSelect',
      options: ageGroupOptions,
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (node) => {
    setSelectedRowId(node._id);
    setFormTemplate(buildFormTemplate(node._id));
    // Prefilled with the current values, including explicit `null`s. The update DTO reads
    // an absent `parentId`/`ageGroupIds` as "leave unchanged" and a `null` as "clear it",
    // and since every field is always sent from here, a cleared select really does clear.
    // `ageGroupIds` goes as `[]` rather than `null` when empty — both clear it server-side,
    // and the multi-select input needs an array to render against.
    setFormData({
      _id: node._id,
      value: node.value,
      level: node.level,
      parentId: node.parentId || null,
      ageGroupIds: node.ageGroupIds || [],
    });
    openFormModal();
  };

  const handleCreateChild = (parent) => {
    setSelectedRowId(null);
    setFormTemplate(buildFormTemplate(null));

    // Default to the next level the framework actually declares below the parent, rather
    // than `parent.level + 1` — frameworks are allowed to leave gaps.
    const nextLevel = levelOptions.find(({ value }) => value > parent.level);
    setFormData({
      value: null,
      level: nextLevel ? nextLevel.value : null,
      parentId: parent._id,
      ageGroupIds: [],
    });
    openFormModal();
  };

  const handleCreateRoot = () => {
    setSelectedRowId(null);
    setFormTemplate(buildFormTemplate(null));
    setFormData({
      value: null,
      level: levelOptions.length ? levelOptions[0].value : null,
      parentId: null,
      ageGroupIds: [],
    });
    openFormModal();
  };

  const handleSave = async () => {
    handleLoader(true);

    dispatch(
      selectedRowId === null
        ? createCurriculumNode(sanitizePayload({ ...formData, frameworkId }))
        : updateCurriculumNode(formData)
    )
      .unwrap()
      .then(() => {
        SuccessToast(selectedRowId === null ? 'Node created' : 'Node saved');
        refresh();
        closeFormModal();
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  // Already narrowed server-side by whatever shallower filters are set — see the fetch
  // effect above — so this level's options are just whatever came back for it.
  const filterOptionsForLevel = (level) => nodesByLevel[level] || [];

  // Picking (or clearing) a filter drops every deeper filter — they may no longer belong
  // to a node under the new selection. Filter values are a node's `value` string, not its
  // id: that's what the `level<N>Item` query param matches against.
  const setAncestorFilter = (level, filterValue) => {
    setAncestorFilters((state) => {
      const next = {};
      Object.entries(state).forEach(([lvl, val]) => {
        if (Number(lvl) < level) next[lvl] = val;
      });
      if (filterValue) next[level] = filterValue;
      return next;
    });
  };

  const levelNodes = nodesByLevel[selectedLevel] || [];

  const displayedNodes = search.trim()
    ? levelNodes.filter(({ value }) =>
        value.toLowerCase().includes(search.trim().toLowerCase())
      )
    : levelNodes;

  const shallowerLevels = levelOptions.filter(({ value }) => value < selectedLevel);

  // Root nodes only ever belong at the framework's shallowest declared level, and a node
  // at the deepest declared level has nowhere further down to add a child — so both
  // actions are tab-scoped rather than always shown.
  const isShallowestLevelSelected =
    levelOptions.length > 0 && selectedLevel === levelOptions[0].value;
  const isDeepestLevelSelected =
    levelOptions.length > 0 &&
    selectedLevel === levelOptions[levelOptions.length - 1].value;

  return (
    <Box>
      <Box style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
        {isShallowestLevelSelected && (
          <Button
            onClick={handleCreateRoot}
            variant={'contained'}
            color="primary"
            disabled={!frameworkId}>
            Add Root Node
          </Button>
        )}
        <Box style={{ marginLeft: 'auto' }}>
          <CurriculumFrameworkPicker value={frameworkId} onChange={setFrameworkId} />
        </Box>
      </Box>

      {!frameworkId ? (
        <FrameworkRequiredNotice>
          Pick a framework to browse and edit its node tree.
        </FrameworkRequiredNotice>
      ) : (
        <>
          <Box mt={2} display="flex" justifyContent="flex-end">
            <CurriculumBulkImport
              frameworkId={frameworkId}
              label="Nodes"
              templateFilename="curriculum-nodes-import-template.xlsx"
              getTemplate={getCurriculumNodesImportTemplate}
              importAction={importCurriculumNodes}
              onImported={refresh}
            />
          </Box>

          <Box mt={2}>
            <BuzzTabs
              value={selectedLevel}
              onChange={(e, newLevel) => applyLevelSelection(newLevel)}
              aria-label="curriculum level tabs">
              {levelOptions.map(({ label, value }) => (
                <BuzzTab key={value} label={label} value={value} />
              ))}
            </BuzzTabs>
          </Box>

          <Box
            my={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            style={{ gap: '1rem' }}>
            {shallowerLevels.map(({ label, value: level }) => (
              <Box key={level} style={{ minWidth: '260px' }}>
                <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
                  Filter — {label}
                </Typography>
                <BuzzAutoCompleteSingleSelectInput
                  size="small"
                  placeholder="All"
                  options={filterOptionsForLevel(level).map((node) => {
                    return { label: node.value, value: node.value };
                  })}
                  value={ancestorFilters[level] || null}
                  onChange={(value) => setAncestorFilter(level, value)}
                />
              </Box>
            ))}

            <Typography variant="caption" color="textSecondary">
              {displayedNodes.length} of {levelNodes.length} node
              {levelNodes.length === 1 ? '' : 's'} at this level
            </Typography>

            <Box style={{ minWidth: '260px', marginLeft: 'auto' }}>
              <BuzzInput
                type="text"
                size="small"
                placeholder="Search node values"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Box>

          <Box border={1} borderColor="rgba(0, 0, 0, 0.12)" borderRadius={4}>
            {displayedNodes.map((node) => (
              <NodeListItem
                key={node._id}
                node={node}
                ageGroupLabels={ageGroupLabels}
                canAddChild={!isDeepestLevelSelected}
                onAddChild={handleCreateChild}
                onEdit={handleEdit}
              />
            ))}

            {levelNodes.length === 0 && (
              <FrameworkRequiredNotice>
                No nodes at this level yet.
              </FrameworkRequiredNotice>
            )}
          </Box>
        </>
      )}

      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Node'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}

export default AkademyCurriculumNodes;
